import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import history from '@history';
import { FormsPaths } from 'app/configs/routesConfig/forms';
import { FormBuilderSkeleton } from 'app/pages/system/forms';
import withLoading from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { FormViewerNavigationType } from 'constants/forms';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoading from 'hooks/useLoading';
import { isPdfFormType } from 'utils/forms';

import {
  getFormNewApiTemplateAction,
  getFormTemplateFromHistoryAction,
  getPdfFormTemplateFromHistoryAction,
} from 'store/forms/actions';
import { selectFormNavigationType, selectFormTemplate } from 'store/forms/selectors';

// Due to the limitations of this rule in hocs
/* eslint-disable react/prop-types */
const withFormTemplateContent = WrappedComponent => {
  const EnhancedComponent = props => {
    const dispatch = useDispatch();
    const pathParams = useParams();

    const navigationType = useSelector(selectFormNavigationType);
    const { formParams } = props;
    const { formId, historyEntityId, formType } =
      navigationType === FormViewerNavigationType.STATE ? formParams : pathParams;

    const [isLoading, setIsLoading] = useLoading(false);

    if (!formId) {
      throw new Error('Form id not available, but expected!');
    }

    const form = useSelector(selectFormTemplate);

    const formRef = useRef(form);
    const isFormSetted = form?.formId === formId;

    useEffect(() => {
      formRef.current = form;
    }, [form]);

    useEffectSafeStrictMode(() => {
      if (formRef.current?.formId === formId) {
        return;
      }

      setIsLoading(true);

      dispatch(getFormNewApiTemplateAction({ formId }))
        .catch(() => FormsPaths.forms.navigateWith(history.push))
        .finally(() => setIsLoading(false));
    }, [dispatch, formId, setIsLoading]);

    useEffectSafeStrictMode(() => {
      if (!historyEntityId) {
        return;
      }

      setIsLoading(true);

      const action = isPdfFormType(formType)
        ? getPdfFormTemplateFromHistoryAction
        : getFormTemplateFromHistoryAction;

      dispatch(
        action({
          formId,
          entryId: historyEntityId,
        }),
      )
        .catch(Error)
        .finally(() => setIsLoading(false));
    }, [dispatch, formId, historyEntityId, formType, setIsLoading]);

    // Due to the limitations of this rule in hocs

    return isLoading || !form || !isFormSetted ? (
      <FormBuilderSkeleton />
    ) : (
      <WrappedComponent form={form} {...props} />
    );
  };

  EnhancedComponent.displayName = `withFormContent${
    WrappedComponent.displayName || WrappedComponent.name
  }`;

  return withLoading(EnhancedComponent);
};

export const WithFormContentPropTypes = {
  form: PropTypes.object,
  formParams: PropTypes.shape({
    formId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    formType: PropTypes.string.isRequired,
    selectedPage: PropTypes.number.isRequired,
    historyEntityId: PropTypes.string,
  }),
};

export default withFormTemplateContent;
